import React from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'
import useCompaniesForSource from '@/hooks/useCompaniesForSource'
import type { Company } from '@/types/dashboard-types'
import { DEFAULT_DATA_SOURCE, type DataSource } from '@/types/data-source'
import { companyLabelWithHierarchy } from '@/utils/companyHierarchy'

interface Params {
  value?: number | number[]
  onChange: (companyId?: number | number[]) => void
  isMultiSelect?: boolean
  filter?: (c: Company) => boolean
  /** Which back end to list companies from; defaults to Fresh. */
  dataSource?: DataSource
}

export const CompanyDropdown: React.FC<Params> = ({ value, onChange, isMultiSelect, filter, dataSource }) => {
  const [single, setSingle] = React.useState<Company>()
  const { companies } = useCompaniesForSource(dataSource ?? DEFAULT_DATA_SOURCE)
  const visibleCompanies = React.useMemo(() => (filter ? companies.filter(filter) : companies), [companies, filter])
  React.useEffect(() => {
    if (visibleCompanies.length === 1) {
      onChange(Number(visibleCompanies[0].id))
      setSingle(visibleCompanies[0])
    }
  }, [visibleCompanies])
  if (single) {
    return (
      <div>
        <label>Company</label>
        <div className="mt-1 font-bold">{single.name}</div>
      </div>
    )
  }
  const options = visibleCompanies.map((c) => ({
    label: c.name,
    value: String(c.id),
    fullName: c.fullName ?? companyLabelWithHierarchy(c, companies),
  }))
  const selectedItem = isMultiSelect
    ? options.filter((c) => Array.isArray(value) && value.includes(Number(c.value)))
    : options.find((c) => Number(c.value) === value) || { label: '', value: '' }

  return (
    <div>
      <label>Company</label>
      <Dropdown
        options={options}
        handleSingleSelectItem={(option: DropdownOption | null) => {
          const newCompanyId = option && option.value ? Number(option.value) : undefined
          if (newCompanyId) {
            onChange(Number(newCompanyId))
          } else {
            onChange(undefined)
          }
        }}
        handleMultiSelectItem={(opt: DropdownOption[]) => {
          const selectedValues = opt.filter((option) => option.value !== '').map((option) => option.value)
          const selectedCompanies = selectedValues.length > 0 ? selectedValues.map((v) => Number(v)) : undefined
          onChange(selectedCompanies)
        }}
        selectedItem={selectedItem}
        placeholder="Select company..."
        isMultiSelect={isMultiSelect}
      />
    </div>
  )
}
